/**
 * V112-3-10: the scope screen's fixed save bar and the preview sheet attached to it (findings R1 #5–#6, R2 #1, and
 * the built-panel review).
 *
 * - The bar shows while there are unsaved edits: "N changes · Markets", Discard, Review changes. It sits inside the
 *   content column (not over the sidebar) and above the phone tab bar (measured), and its height is reserved below
 *   the page and as scroll padding, so a focused control never hides behind it.
 * - Review changes opens the preview as a sheet above the bar, a modal dialog: everything else inert, focus on the
 *   heading, Esc goes back, a scrim click returns focus to the sheet. Confirm is the only action that writes, and it
 *   stays unavailable until the preview has loaded (a failed preview shows the error with Check again).
 * - Polite live regions: the unsaved count (with any page message), and inside the sheet the preview result.
 * - One leave guard for every way off the screen: links (the sheet's too), browser Back (a history entry held while
 *   there are edits), moves registered through scope-guard (the system tabs), and page unload.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Field, Input } from '@aigovops/design-system';
import { plural } from './scope-options';
import { setScopeGuard } from './scope-guard';

export interface ScopeSaveBarProps {
  /** The changed answers' names, in screen order (the count is their length). */
  names: string[];
  open: boolean;
  /** The sheet heading (`id="sc-pv-h"` is set here). */
  title?: ReactNode;
  /** The result in words, announced inside the sheet once the preview has loaded. */
  resultText?: string;
  /** Under the heading: counts, the changes in words, "needs a sector" lines. */
  head?: ReactNode;
  /** The sheet body: rule lines per system. */
  children?: ReactNode;
  /** Where the optional reason is shown ("Data classes, Who has the final say?"); omit to offer no reason. */
  reasonWhere?: string;
  reason: string;
  onReason: (v: string) => void;
  busy?: boolean;
  /** A preview is loading: Confirm waits. */
  loading?: boolean;
  /** The preview loaded (Confirm is available only then). */
  ready?: boolean;
  /** The preview (or the save) failed: shown in the sheet with a retry that matches the failure. */
  error?: string | null;
  onRetry?: () => void;
  /** "Check again" re-runs a failed preview; "Try again" re-runs a failed save. */
  retryLabel?: string;
  /** Unsaved answers that save on their own (e.g. "Your role"): they hold the leave guard but aren't in the batch. */
  held?: string[];
  onReview: () => void;
  onDiscard: () => void;
  onBack: () => void;
  onConfirm: () => void;
  /** A message to announce (e.g. "Changes discarded."); `n` makes a repeat announce again. */
  announcement?: { text: string; n: number } | null;
}

type Leave = { kind: 'link'; a: HTMLAnchorElement } | { kind: 'go'; go: () => void } | { kind: 'back' };

function useKeyboardMode() {
  useEffect(() => {
    const html = document.documentElement;
    const kbd = (e: KeyboardEvent) => { if (!e.metaKey && !e.ctrlKey && !e.altKey) html.setAttribute('data-sc-kbd', ''); };
    const ptr = () => html.removeAttribute('data-sc-kbd');
    document.addEventListener('keydown', kbd, true);
    document.addEventListener('pointerdown', ptr, true);
    return () => { document.removeEventListener('keydown', kbd, true); document.removeEventListener('pointerdown', ptr, true); html.removeAttribute('data-sc-kbd'); };
  }, []);
}

/** Mark every body child except `keep` inert; returns the undo. */
function inertOthers(keep: Element[]): () => void {
  const others = [...document.body.children].filter((el) => !keep.includes(el) && !el.hasAttribute('inert')) as HTMLElement[];
  others.forEach((el) => el.setAttribute('inert', ''));
  return () => others.forEach((el) => el.removeAttribute('inert'));
}
/** Keep Tab inside `root`. */
function trapTab(e: KeyboardEvent, root: HTMLElement | null) {
  if (e.key !== 'Tab' || !root) return;
  const items = [...root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])')]
    .filter((el) => !el.hasAttribute('disabled') && !el.closest('[hidden]') && !el.closest('[inert]'));
  if (!items.length) return;
  const first = items[0]!, last = items[items.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
}

export function ScopeSaveBar(p: ScopeSaveBarProps) {
  const n = p.names.length;
  const held = p.held ?? [];
  /** Unsaved anything (the batch or an answer that saves on its own): the leave guard holds. */
  const pending = n + held.length;
  const shown = p.open || n > 0;
  const [host] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')));
  const cardRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const leaveRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLButtonElement>(null);
  const [live, setLive] = useState('');
  const [sheetLive, setSheetLive] = useState('');
  const [reasonOpen, setReasonOpen] = useState(false);
  const [leave, setLeave] = useState<Leave | null>(null);
  /** Where focus was when the leave dialog opened (restored on Stay / Esc). */
  const leaveFrom = useRef<HTMLElement | null>(null);
  const allowLeave = useRef(false);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  useKeyboardMode();

  useLayoutEffect(() => {
    if (!host) return;
    host.setAttribute('data-scope-dock', '');
    host.className = 'sc-portal';
    document.body.appendChild(host);
    return () => { host.remove(); };
  }, [host]);

  // Live region: a page message and the unsaved count, joined when both change in one render.
  const lastN = useRef(n);
  const lastMsg = useRef<number | null>(null);
  useEffect(() => {
    const parts: string[] = [];
    if (p.announcement && p.announcement.n !== lastMsg.current) { lastMsg.current = p.announcement.n; parts.push(p.announcement.text); }
    if (n !== lastN.current) {
      lastN.current = n;
      if (n) parts.push(`${plural(n, 'unsaved change', 'unsaved changes')}. Review changes to see what ${n === 1 ? 'it adds or removes' : 'they add or remove'}.`);
    }
    if (parts.length) setLive(parts.join(' '));
  }, [n, p.announcement]);
  // Inside the sheet: the preview result once it has loaded.
  useEffect(() => {
    if (!p.open) { setSheetLive(''); return; }
    if (p.loading) setSheetLive('Checking what this changes…');
    else if (p.error) setSheetLive(p.error);
    else if (p.resultText) setSheetLive(p.resultText);
  }, [p.open, p.loading, p.error, p.resultText]);

  // Place the dock inside the content column and above the phone tab bar; reserve its height under the page.
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (!shown || !host) { html.classList.remove('sc-dock-on'); return undefined; }
    html.classList.add('sc-dock-on');
    const place = () => {
      const tab = document.querySelector<HTMLElement>('[data-mobile-tabbar]');
      const main = document.getElementById('main-content');
      const bottom = tab ? Math.round(tab.getBoundingClientRect().height) : 0;
      const r = main?.getBoundingClientRect();
      const left = r && !tab && r.width > 0 ? Math.max(0, Math.round(r.left)) : 0;
      const right = r && !tab && r.width > 0 ? Math.max(0, Math.round(window.innerWidth - r.right)) : 0;
      for (const el of [host, html]) {
        el.style.setProperty('--sc-dock-bottom', `${bottom}px`);
        el.style.setProperty('--sc-dock-safe', tab ? '0px' : 'env(safe-area-inset-bottom, 0px)');
      }
      host.style.setProperty('--sc-dock-left', `${left}px`);
      host.style.setProperty('--sc-dock-right', `${right}px`);
      if (!p.open && cardRef.current) html.style.setProperty('--sc-dock-h', `${cardRef.current.offsetHeight + 24}px`);
    };
    place();
    window.addEventListener('resize', place);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (ro && cardRef.current) ro.observe(cardRef.current);
    const main = document.getElementById('main-content');
    if (ro && main) ro.observe(main);
    return () => { window.removeEventListener('resize', place); ro?.disconnect(); html.classList.remove('sc-dock-on'); };
  }, [shown, p.open, host]);

  // Modal while the sheet is open: everything else inert; focus to the heading; Esc goes back.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!host) return;
    if (p.open) {
      const undo = inertOthers([host]);
      wasOpen.current = true;
      requestAnimationFrame(() => document.getElementById('sc-pv-h')?.focus());
      return undo;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      setReasonOpen(false);
      reviewRef.current?.focus();
    }
    return undefined;
  }, [p.open, host]);
  useEffect(() => {
    if (!p.open || leave) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); p.onBack(); } else trapTab(e, cardRef.current); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [p.open, p.onBack, leave]);

  // ---- Leave guard ----
  const nRef = useRef(pending);
  nRef.current = pending;
  const openLeave = (l: Leave) => {
    leaveFrom.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    setLeave(l);
  };
  useEffect(() => {
    if (!pending) return;
    // Links anywhere (the sheet's included), in the capture phase, before the router sees the click.
    const onClick = (e: MouseEvent) => {
      if (allowLeave.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download') || leaveRef.current?.contains(a)) return;
      const href = a.getAttribute('href') ?? '';
      if (href.startsWith('#')) return;
      e.preventDefault();
      e.stopPropagation();
      openLeave({ kind: 'link', a });
    };
    const onUnload = (e: BeforeUnloadEvent) => { if (!allowLeave.current) { e.preventDefault(); e.returnValue = ''; } };
    // Browser Back: hold one history entry while there are edits; going back from it asks first.
    const held = () => (window.history.state as { scGuard?: boolean } | null)?.scGuard === true;
    if (!held()) window.history.pushState({ ...(window.history.state ?? {}), scGuard: true }, '', window.location.href);
    const onPop = () => {
      if (allowLeave.current || held()) return;
      window.history.pushState({ ...(window.history.state ?? {}), scGuard: true }, '', window.location.href);
      openLeave({ kind: 'back' });
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('popstate', onPop);
    // Moves without a link (the system page's tabs).
    setScopeGuard((go) => (nRef.current ? openLeave({ kind: 'go', go }) : go()));
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('popstate', onPop);
      setScopeGuard(null);
      // Edits saved or discarded on this page: drop the held entry (the URL doesn't change).
      if (held() && !allowLeave.current) {
        allowLeave.current = true;
        const release = () => { allowLeave.current = false; window.removeEventListener('popstate', release); };
        window.addEventListener('popstate', release);
        window.history.back();
      }
    };
  }, [pending > 0]);

  // The leave dialog is modal: everything else inert, Tab stays inside, Esc stays.
  const stayRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!leave || !host) return;
    const undo = inertOthers([host]);
    dockRef.current?.setAttribute('inert', '');
    stayRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeLeave(true); }
      else trapTab(e, leaveRef.current);
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); dockRef.current?.removeAttribute('inert'); undo(); };
  }, [leave, host]);
  /** Close the leave dialog; on Stay / Esc, focus goes back where it was (the link, the tab button, Review changes),
   *  or for Back to the selected tab / the page heading. */
  const closeLeave = (refocus: boolean) => {
    const l = leave;
    const from = leaveFrom.current;
    setLeave(null);
    if (!refocus) return;
    window.setTimeout(() => {
      const target = l?.kind === 'link' && l.a.isConnected ? l.a
        : from?.isConnected ? from
          : document.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]') ?? document.querySelector<HTMLElement>('h1');
      target?.focus();
    }, 0);
  };
  const discardAndLeave = () => {
    const l = leave;
    setLeave(null);
    p.onDiscard();
    if (!l) return;
    // Leaving forward: the held Back entry isn't needed any more (same URL; drop its mark).
    if (l.kind !== 'back' && (window.history.state as { scGuard?: boolean } | null)?.scGuard) {
      const { scGuard: _g, ...rest } = window.history.state as Record<string, unknown>;
      window.history.replaceState(rest, '', window.location.href);
    }
    allowLeave.current = true;
    if (l.kind === 'link') { l.a.click(); allowLeave.current = false; }
    else if (l.kind === 'go') { l.go(); allowLeave.current = false; }
    else window.history.go(-2); // past the held entry to the page before
  };

  if (!host) return null;
  const confirmBlocked = p.busy || p.loading || !p.ready;
  const dock = shown ? (
    <>
      {p.open ? <div className="sc-scrim" onClick={() => document.getElementById('sc-pv-h')?.focus()} /> : null}
      <div className="sc-dock" ref={dockRef}>
        <div
          ref={cardRef}
          className="sc-dock-card"
          data-open={p.open}
          role={p.open ? 'dialog' : 'region'}
          aria-modal={p.open ? true : undefined}
          aria-labelledby={p.open ? 'sc-pv-h' : undefined}
          aria-label={p.open ? undefined : 'Unsaved changes'}
        >
          {p.open ? (
            <section className="sc-sheet">
              <header className="sc-sheet-head">
                <h2 id="sc-pv-h" tabIndex={-1}>{p.loading ? 'Checking what this changes…' : p.title}</h2>
                {p.loading || !p.ready ? null : p.head}
              </header>
              <div className="sc-sheet-body">
                {p.error ? (
                  <div className="sc-sheet-error" role="alert">
                    <p>{p.error}</p>
                    {p.onRetry ? <Button compact onClick={p.onRetry}>{p.retryLabel ?? 'Check again'}</Button> : null}
                  </div>
                ) : null}
                {p.loading || !p.ready ? null : p.children}
                {p.reasonWhere && p.ready && !p.loading ? (
                  reasonOpen ? (
                    <div className="sc-reason">
                      <Field id={`${uid}-reason`} label="Reason (optional)" hint={`Shown on: ${p.reasonWhere}`}>
                        <Input id={`${uid}-reason`} autoFocus value={p.reason} maxLength={280} autoComplete="off" onChange={(e) => p.onReason(e.target.value)} />
                      </Field>
                    </div>
                  ) : (
                    <div><button type="button" className="sc-link" aria-expanded={false} onClick={() => setReasonOpen(true)}>Add a reason</button></div>
                  )
                ) : null}
                <p className="sc-sheet-note">Nothing changes until you confirm.</p>
              </div>
            </section>
          ) : null}
          <div className="sc-savebar">
            <p><b>{plural(n, 'change', 'changes')}</b>{n ? <span className="sc-muted">{` · ${p.names.join(', ')}`}</span> : null}
              {held.length ? <span className="sc-muted">{` · ${held.map((h) => `${h} (saves separately)`).join(', ')}`}</span> : null}</p>
            {p.open ? (
              <div className="sc-acts">
                <Button onClick={p.onBack}>Back to edits</Button>
                <Button variant="primary" aria-disabled={confirmBlocked || undefined} aria-describedby={!p.ready && !p.loading ? `${uid}-blocked` : undefined}
                  onClick={() => { if (!confirmBlocked) p.onConfirm(); }}>
                  {p.busy ? 'Saving…' : 'Confirm'}
                </Button>
                {!p.ready && !p.loading ? <span id={`${uid}-blocked`} className="sr-only">Check again first</span> : null}
              </div>
            ) : (
              <div className="sc-acts">
                <Button onClick={p.onDiscard}>Discard</Button>
                <Button ref={reviewRef} variant="primary" onClick={p.onReview}>Review changes</Button>
              </div>
            )}
          </div>
          {p.open ? <div className="sr-only" role="status" aria-live="polite">{sheetLive}</div> : null}
        </div>
      </div>
    </>
  ) : null;

  return (
    <>
      {createPortal(
        <>
          {dock}
          <div className="sr-only" role="status" aria-live="polite">{live}</div>
          {leave ? (
            <div className="sc-leave">
              <div ref={leaveRef} className="sc-leave-card" role="alertdialog" aria-modal={true} aria-labelledby={`${uid}-leave-h`} aria-describedby={`${uid}-leave-t`}>
                <h2 id={`${uid}-leave-h`}>Leave without saving?</h2>
                <p id={`${uid}-leave-t`}>{`You have ${plural(pending, 'unsaved change', 'unsaved changes')}${held.length ? ` (including ${held.join(', ')}, which saves separately)` : ''}. Nothing applies until you save it.`}</p>
                <div className="sc-acts">
                  {n ? (
                    <Button variant="primary" onClick={() => {
                      closeLeave(false);
                      if (!p.open) p.onReview(); else window.setTimeout(() => document.getElementById('sc-pv-h')?.focus(), 0);
                    }}>Review changes</Button>
                  ) : null}
                  <Button onClick={discardAndLeave}>Discard and leave</Button>
                  <Button ref={stayRef} onClick={() => closeLeave(true)}>Stay</Button>
                </div>
              </div>
            </div>
          ) : null}
        </>,
        host,
      )}
    </>
  );
}
